import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { ROUTES } from "@/shared/config/constants";

export const CookiesPage: React.FC = () => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language === "es";

  return (
    <div className="min-h-screen bg-romance-50">
      {/* Header */}
      <header className="bg-tiber-900 text-white py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to={ROUTES.HOME}
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            {isSpanish ? "Volver al inicio" : "Back to home"}
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-4xl">🍪</span>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">
                {isSpanish ? "Política de Cookies" : "Cookie Policy"}
              </h1>
              <p className="text-white/70 font-light mt-1">
                {isSpanish
                  ? "Última actualización: Enero 2026"
                  : "Last updated: January 2026"}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12">
          {isSpanish ? (
            <>
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-tiber-900 mb-4">
                  1. ¿Qué son las Cookies?
                </h2>
                <p className="text-tiber-700 font-light leading-relaxed">
                  Las cookies son pequeños archivos de texto que se almacenan en
                  tu dispositivo cuando visitas un sitio web. Nos ayudan a
                  recordar tus preferencias, mejorar tu experiencia y analizar
                  cómo se utiliza nuestra plataforma.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-tiber-900 mb-4">
                  2. Tipos de Cookies que Utilizamos
                </h2>

                <div className="space-y-6">
                  <div className="border-l-4 border-green-500 pl-4">
                    <h3 className="font-bold text-tiber-900">
                      🔒 Cookies Esenciales
                    </h3>
                    <p className="text-tiber-700 font-light text-sm mt-1">
                      Necesarias para el funcionamiento básico de la plataforma.
                    </p>
                    <ul className="list-disc pl-6 text-tiber-600 font-light text-sm mt-2 space-y-1">
                      <li>Autenticación de sesión</li>
                      <li>Preferencias de idioma</li>
                      <li>Configuración de seguridad</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-tiber-500 pl-4">
                    <h3 className="font-bold text-tiber-900">
                      📊 Cookies de Análisis
                    </h3>
                    <p className="text-tiber-700 font-light text-sm mt-1">
                      Nos ayudan a entender cómo interactúas con la plataforma.
                    </p>
                    <ul className="list-disc pl-6 text-tiber-600 font-light text-sm mt-2 space-y-1">
                      <li>PostHog (análisis de uso)</li>
                      <li>Métricas de rendimiento</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-tiber-500 pl-4">
                    <h3 className="font-bold text-tiber-900">
                      ⚙️ Cookies de Funcionalidad
                    </h3>
                    <p className="text-tiber-700 font-light text-sm mt-1">
                      Permiten funciones adicionales y personalización.
                    </p>
                    <ul className="list-disc pl-6 text-tiber-600 font-light text-sm mt-2 space-y-1">
                      <li>Preferencias de visualización</li>
                      <li>Historial de documentos recientes</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-tiber-900 mb-4">
                  3. Cookies de Terceros
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-tiber-100">
                      <tr>
                        <th className="px-4 py-2 text-left font-bold text-tiber-900">
                          Servicio
                        </th>
                        <th className="px-4 py-2 text-left font-bold text-tiber-900">
                          Propósito
                        </th>
                        <th className="px-4 py-2 text-left font-bold text-tiber-900">
                          Duración
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-tiber-100">
                      <tr>
                        <td className="px-4 py-3 font-light">Platform</td>
                        <td className="px-4 py-3 font-light">Autenticación</td>
                        <td className="px-4 py-3 font-light">Sesión</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 font-light">Stripe</td>
                        <td className="px-4 py-3 font-light">Pagos</td>
                        <td className="px-4 py-3 font-light">1 año</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 font-light">PostHog</td>
                        <td className="px-4 py-3 font-light">Análisis</td>
                        <td className="px-4 py-3 font-light">1 año</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-tiber-900 mb-4">
                  4. Contacto
                </h2>
                <p className="text-tiber-700 font-light leading-relaxed">
                  Para consultas sobre cookies:{" "}
                  <a
                    href="mailto:nolimstudios@gmail.com"
                    className="text-tiber-600 underline"
                  >
                    Nolim Studios Support
                  </a>
                </p>
              </section>
            </>
          ) : (
            <>
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-tiber-900 mb-4">
                  1. What are Cookies?
                </h2>
                <p className="text-tiber-700 font-light leading-relaxed">
                  Cookies are small text files stored on your device when you
                  visit a website. They help us remember your preferences and
                  improve your experience.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-tiber-900 mb-4">
                  2. Types of Cookies We Use
                </h2>

                <div className="space-y-6">
                  <div className="border-l-4 border-green-500 pl-4">
                    <h3 className="font-bold text-tiber-900">
                      🔒 Essential Cookies
                    </h3>
                    <p className="text-tiber-700 font-light text-sm mt-1">
                      Necessary for basic platform functionality.
                    </p>
                    <ul className="list-disc pl-6 text-tiber-600 font-light text-sm mt-2 space-y-1">
                      <li>Session authentication</li>
                      <li>Language preferences</li>
                      <li>Security settings</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-tiber-500 pl-4">
                    <h3 className="font-bold text-tiber-900">
                      📊 Analytics Cookies
                    </h3>
                    <p className="text-tiber-700 font-light text-sm mt-1">
                      Help us understand how you interact with the platform.
                    </p>
                    <ul className="list-disc pl-6 text-tiber-600 font-light text-sm mt-2 space-y-1">
                      <li>PostHog (usage analytics)</li>
                      <li>Performance metrics</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-tiber-500 pl-4">
                    <h3 className="font-bold text-tiber-900">
                      ⚙️ Functionality Cookies
                    </h3>
                    <p className="text-tiber-700 font-light text-sm mt-1">
                      Enable additional features and personalization.
                    </p>
                    <ul className="list-disc pl-6 text-tiber-600 font-light text-sm mt-2 space-y-1">
                      <li>Display preferences</li>
                      <li>Recent documents history</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-tiber-900 mb-4">
                  3. Third-Party Cookies
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-tiber-100">
                      <tr>
                        <th className="px-4 py-2 text-left font-bold text-tiber-900">
                          Service
                        </th>
                        <th className="px-4 py-2 text-left font-bold text-tiber-900">
                          Purpose
                        </th>
                        <th className="px-4 py-2 text-left font-bold text-tiber-900">
                          Duration
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-tiber-100">
                      <tr>
                        <td className="px-4 py-3 font-light">Platform</td>
                        <td className="px-4 py-3 font-light">Authentication</td>
                        <td className="px-4 py-3 font-light">Session</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 font-light">Stripe</td>
                        <td className="px-4 py-3 font-light">Payments</td>
                        <td className="px-4 py-3 font-light">1 year</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 font-light">PostHog</td>
                        <td className="px-4 py-3 font-light">Analytics</td>
                        <td className="px-4 py-3 font-light">1 year</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-tiber-900 mb-4">
                  4. Contact
                </h2>
                <p className="text-tiber-700 font-light leading-relaxed">
                  For cookie inquiries:{" "}
                  <a
                    href="mailto:nolimstudios@gmail.com"
                    className="text-tiber-600 underline"
                  >
                    Nolim Studios Support
                  </a>
                </p>
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  );
};
